import { IconProp } from "@fortawesome/fontawesome-svg-core"
import { FusionColor } from "../../../theme/FusionStyle"
import { ConceptStatusValidityInfo, SyncItemStatus, SyncResultStatus, ValiditySeverity } from "@fusion-advantage/formlib"
import { faCircleCheck as farCircleCheck, faCircleExclamation as farCircleExclamation, faSpinnerThird, faRotateExclamation } from "@fortawesome/pro-regular-svg-icons"
import { faBug, faCircleExclamation as fasCircleExclamation, faCircleX, faCircleMinus, faCircleCheck as fasCircleCheck, faCircleArrowDown, faCircleArrowUp, faLocationCheck, faLocationDotSlash, faLocationQuestion, faLocationMinus, faLocationExclamation, faArrowsRotate } from "@fortawesome/pro-solid-svg-icons"
import { ConceptStatusValidity } from "@fusion-advantage/backcompat"

export interface StatusIndicatorProps {
	icon: IconProp
	color: FusionColor
	title?: string
}

export enum StatusIndicatorContext {
	Modal = 'modal',
	AppHeaderFeature = 'appHeaderFeature',
}

export const validityIndicatorProps = (validity: ConceptStatusValidity): StatusIndicatorProps => {
	const validityInfo = new ConceptStatusValidityInfo(validity)
	return validitySeverityIndicatorProps(validityInfo.severity)
}

export const validitySeverityIndicatorProps = (severity: ValiditySeverity): StatusIndicatorProps => {
	switch(severity) {
		case ValiditySeverity.Success:
			return {
				icon: farCircleCheck,
				color: FusionColor.GoGreen,
			}
		case ValiditySeverity.Warning:
			return {
				icon: farCircleExclamation,
				color: FusionColor.OrangeMain,
			}
		case ValiditySeverity.Error:
			return {
				icon: faCircleX,
				color: FusionColor.StopRed,
			}
		default:
			return {
				icon: faBug,
				color: FusionColor.StopRed,
			}
	}
}

export const syncStatusIndicatorProps = (status: SyncItemStatus|SyncResultStatus, context: StatusIndicatorContext): StatusIndicatorProps => {
	let props: StatusIndicatorProps
	switch(status) {
		case SyncItemStatus.NoConnection:
		case SyncResultStatus.NoConnection:
			props = {
				icon: faRotateExclamation,
				color: FusionColor.OrangeMain,
			}
			if(context === StatusIndicatorContext.AppHeaderFeature) {
				props.icon = faArrowsRotate
			}
			return props
		case SyncItemStatus.NotModified:
		case SyncItemStatus.Ok:
		case SyncResultStatus.Ok:
			props = {
				icon: fasCircleCheck,
				color: FusionColor.GoGreen,
			}
			if(context === StatusIndicatorContext.AppHeaderFeature) {
				props = {
					icon: faArrowsRotate,
					color: FusionColor.Inherit,
				}
			}
			return props
		case SyncItemStatus.DownloadOk:
			return {
				icon: faCircleArrowDown,
				color: FusionColor.GoGreen,
			}
		case SyncItemStatus.UploadOk:
			return {
				icon: faCircleArrowUp,
				color: FusionColor.GoGreen,
			}
		case SyncItemStatus.Pending:
		case SyncResultStatus.Pending:
			return {
				icon: faSpinnerThird,
				color: FusionColor.Inherit,
			}
		case SyncItemStatus.Canceled:
			return {
				icon: faCircleMinus,
				color: FusionColor.InputLabel,
			}
		default:
			props = {
				icon: fasCircleExclamation,
				color: FusionColor.StopRed,
			}
			if(context === StatusIndicatorContext.AppHeaderFeature) {
				props.icon = faArrowsRotate
			}
			return props
	}
}

export enum LocationStatus {
	NoBrowserSupport = 'Unsupported by Browser',
	ServicesDisabled = 'Services Disabled',
	Prompt = 'Prompt',
	Denied = 'Denied',
	Querying = 'Querying',      // is querying; no previously queried location
	Refreshing = 'Refreshing',  // is querying, but can supply previously queried location
	Success = 'Success',
	Unknown = 'Unknown',
}

export const locationIndicatorProps = (status: LocationStatus|null, context: StatusIndicatorContext): StatusIndicatorProps => {
	let props: StatusIndicatorProps
	switch(status) {
		case LocationStatus.NoBrowserSupport:
		case LocationStatus.ServicesDisabled:
			props = {
				icon: faLocationDotSlash,
				color: FusionColor.InputLabel,
				title: status,
			}
			if(context === StatusIndicatorContext.AppHeaderFeature) {
				props.color = FusionColor.StopRed
			}
			break
		case LocationStatus.Prompt:
			props = {
				icon: faLocationQuestion,
				color: FusionColor.OrangeMain,
				title: 'Required',
			}
			break
		case LocationStatus.Denied:
			props = {
				icon: faLocationMinus,
				color: FusionColor.StopRed,
				title: status,
			}
			break
		case LocationStatus.Querying:
		case LocationStatus.Refreshing:
			props = {
				icon: faSpinnerThird,
				color: FusionColor.Inherit,
				title: status,
			}
			break
		case LocationStatus.Success:
			props = {
				icon: faLocationCheck,
				color: FusionColor.GoGreen,
				title: 'Granted',
			}
			if(context === StatusIndicatorContext.AppHeaderFeature) {
				props.color = FusionColor.Inherit
			}
			break
		case LocationStatus.Unknown:
		case null:
			props = {
				icon: faLocationExclamation,
				color: FusionColor.StopRed,
				title: status ?? 'Not Yet Queried',
			}
			break
		default:
			props = {
				icon: faBug,
				color: FusionColor.StopRed,
				title: '(Bug)',
			}
			break
	}
	if(context === StatusIndicatorContext.AppHeaderFeature) {
		return {
			icon: props.icon,
			color: props.color,
		}
	}
	return props
}
